import { computed, nextTick, ref, type Ref, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useAuthStore } from "@/stores/auth";
import apiService from "@/services/api";
import { guidedTourSteps, pageDescriptions } from "@/config/tourConfig";

export function useTour() {
	const route = useRoute();
	const router = useRouter();
	const { t } = useI18n();
	const authStore = useAuthStore();
	const sidebarRef = inject<Ref<{ expandGroupForMenuId: (id: string) => void } | null>>("sidebarRef", undefined as any);
	const drawerRef = inject<Ref<boolean>>("drawerRef", undefined as any);
	const tourActiveRef = inject<Ref<boolean>>("tourActiveRef", ref(false));

	const currentRouteName = computed(() => route.name as string);

	/**
	 * Descrição da página atual (para o help button no footer).
	 */
	const pageDescription = computed(() => {
		const name = currentRouteName.value;
		if (!name) return "";
		const config = pageDescriptions[name];
		if (!config) return "";
		return t(config.descriptionKey);
	});

	const hasPageDescription = computed(() => !!pageDescription.value);

	/**
	 * Verifica se o tour guiado já foi completado.
	 */
	const isGuidedTourCompleted = computed(() => {
		return authStore.user?.completedTours?.guidedTour === true;
	});

	/**
	 * Verifica se o tour guiado nunca foi visto (chave não existe).
	 */
	const isFirstVisit = computed(() => {
		const tours = authStore.user?.completedTours;
		if (!tours) return true;
		return !("guidedTour" in tours);
	});

	/**
	 * Expande o grupo do sidebar que contém o menuId.
	 */
	function expandSidebarGroup(menuId: string) {
		sidebarRef?.value?.expandGroupForMenuId(menuId);
	}

	/**
	 * Garante que o sidebar drawer está aberto (necessário em mobile).
	 */
	function ensureDrawerOpen() {
		if (drawerRef?.value === false) {
			drawerRef.value = true;
		}
	}

	/**
	 * Faz scroll do sidebar até o item do menu ficar visível (necessário em telas pequenas).
	 */
	function scrollMenuItemIntoView(menuId: string) {
		const el = document.querySelector(`[data-menu-id="${menuId}"]`);
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}

	/**
	 * Espera um elemento existir no DOM antes de prosseguir.
	 */
	function waitForElement(selector: string, timeout = 2000): Promise<boolean> {
		return new Promise((resolve) => {
			const el = document.querySelector(selector);
			if (el) return resolve(true);

			const observer = new MutationObserver(() => {
				if (document.querySelector(selector)) {
					observer.disconnect();
					resolve(true);
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });

			setTimeout(() => {
				observer.disconnect();
				resolve(false);
			}, timeout);
		});
	}

	/**
	 * Inicia o tour guiado sequencial.
	 * Navega pelo sidebar, destacando cada item de menu.
	 */
	async function startGuidedTour() {
		// Sinalizar que o tour está ativo (impede o drawer de fechar em mobile)
		tourActiveRef.value = true;

		// Garantir que o sidebar está visível
		ensureDrawerOpen();

		// Expandir todos os grupos para que os itens fiquem no DOM visível
		for (const step of guidedTourSteps) {
			expandSidebarGroup(step.menuId);
		}

		const steps: DriveStep[] = guidedTourSteps.map((step) => ({
			element: step.element ?? `[data-menu-id="${step.menuId}"]`,
			popover: {
				title: t(step.titleKey),
				description: t(step.descriptionKey),
				side: "right" as const,
			},
		}));

		let destroyed = false;

		function cleanupTour() {
			if (destroyed) return;
			destroyed = true;
			tourActiveRef.value = false;
			try { driverObj.destroy(); } catch { /* já destruído */ }
		}

		const driverObj = driver({
			showProgress: true,
			animate: true,
			allowClose: true,
			overlayColor: "rgba(0, 0, 0, 0.6)",
			stagePadding: 8,
			stageRadius: 8,
			popoverClass: "ori-tour-popover",
			nextBtnText: t("tours.controls.next"),
			prevBtnText: t("tours.controls.prev"),
			doneBtnText: t("tours.controls.done"),
			progressText: `{{current}} ${t("tours.controls.progressSeparator")} {{total}}`,
			steps,
			onNextClick: () => {
				const currentIndex = driverObj.getActiveIndex()!;
				const nextIndex = currentIndex + 1;
				if (nextIndex >= guidedTourSteps.length) {
					cleanupTour();
					markGuidedTourCompleted(true);
					return;
				}
				const nextStep = guidedTourSteps[nextIndex];
				ensureDrawerOpen();
				expandSidebarGroup(nextStep.menuId);
				router.push(nextStep.route);
				nextTick(() => {
					setTimeout(() => {
						ensureDrawerOpen();
						scrollMenuItemIntoView(nextStep.menuId);
						driverObj.moveNext();
					}, 300);
				});
			},
			onPrevClick: () => {
				const currentIndex = driverObj.getActiveIndex()!;
				const prevIndex = currentIndex - 1;
				if (prevIndex < 0) return;
				const prevStep = guidedTourSteps[prevIndex];
				ensureDrawerOpen();
				expandSidebarGroup(prevStep.menuId);
				router.push(prevStep.route);
				nextTick(() => {
					setTimeout(() => {
						ensureDrawerOpen();
						scrollMenuItemIntoView(prevStep.menuId);
						driverObj.movePrevious();
					}, 300);
				});
			},
			onDestroyStarted: () => {
				cleanupTour();
				markGuidedTourCompleted(true);
			},
		});

		// Navegar para a primeira rota e esperar o elemento existir no DOM
		const firstStep = guidedTourSteps[0];
		router.push(firstStep.route);

		await nextTick();
		const firstSelector = `[data-menu-id="${firstStep.menuId}"]`;
		const found = await waitForElement(firstSelector);

		if (!found) {
			// Elemento não encontrado — não iniciar o tour (não marca como completo)
			tourActiveRef.value = false;
			return;
		}

		// Delay extra para garantir que transições CSS terminaram
		setTimeout(() => {
			ensureDrawerOpen();
			scrollMenuItemIntoView(firstStep.menuId);
			driverObj.drive();
		}, 200);
	}

	/**
	 * Marca o tour guiado como completado (ou skipped) no backend.
	 */
	async function markGuidedTourCompleted(completed: boolean) {
		// Atualiza estado local imediatamente para evitar re-exibição durante a sessão
		if (authStore.user) {
			authStore.user.completedTours = {
				...authStore.user.completedTours,
				guidedTour: completed,
			};
		}
		try {
			const result = await apiService.patch("/auth/tours", {
				tourKey: "guidedTour",
				completed,
			});
			if (authStore.user) {
				authStore.user.completedTours = result.completedTours;
			}
		} catch (err) {
			console.error("Erro ao salvar status do tour:", err);
		}
	}

	return {
		pageDescription,
		hasPageDescription,
		isGuidedTourCompleted,
		isFirstVisit,
		startGuidedTour,
		markGuidedTourCompleted,
	};
}
